import { ProjectMember } from "@/types/Project";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, UserMinus, Crown } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

interface TeamMembersListProps {
  members: ProjectMember[];
  onUpdateRole: (memberId: string, role: 'admin' | 'member' | 'viewer') => Promise<void>;
  onRemoveMember: (memberId: string) => Promise<void>;
  currentUserRole?: string;
}

export function TeamMembersList({
  members,
  onUpdateRole,
  onRemoveMember,
  currentUserRole = "viewer",
}: TeamMembersListProps) {
  const { user } = useAuth();
  const canManageMembers = currentUserRole === "owner" || currentUserRole === "admin";

  const getRoleColor = (role: string) => {
    switch (role) {
      case "owner":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "admin":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "member":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "viewer":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return "??";
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Team Members</h3>
      <div className="space-y-2">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-3 rounded-lg border bg-card"
          >
            <div className="flex items-center space-x-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={member.avatar_url || ""} />
                <AvatarFallback className="text-xs">
                  {getInitials(member.full_name, member.email)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">
                  {member.full_name || member.email}
                  {member.user_id === user?.id && " (You)"}
                </p>
                <p className="text-xs text-muted-foreground">{member.email}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Badge variant="secondary" className={getRoleColor(member.role)}>
                {member.role === "owner" && <Crown className="w-3 h-3 mr-1" />}
                {member.role}
              </Badge>

              {canManageMembers && member.role !== "owner" && member.user_id !== user?.id && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {member.role !== "admin" && (
                      <DropdownMenuItem
                        onClick={() => onUpdateRole(member.id, "admin")}
                      >
                        Make Admin
                      </DropdownMenuItem>
                    )}
                    {member.role !== "member" && (
                      <DropdownMenuItem
                        onClick={() => onUpdateRole(member.id, "member")}
                      >
                        Make Member
                      </DropdownMenuItem>
                    )}
                    {member.role !== "viewer" && (
                      <DropdownMenuItem
                        onClick={() => onUpdateRole(member.id, "viewer")}
                      >
                        Make Viewer
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => onRemoveMember(member.id)}
                      className="text-destructive"
                    >
                      <UserMinus className="w-4 h-4 mr-2" />
                      Remove Member
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}